
const FAQ_ITEMS = [
  {
    question: 'What styles of Jerusalem wall art are available?',
    answer: [
      'Jerusalem wall art at Joodaic falls into three main styles: classic Kotel and Old City scenes, Jerusalem landscape paintings, and modern or abstract interpretations. Classic pieces centre the Western Wall, the Old City gates, and the Jerusalem skyline in a traditional register. Landscape works take a wider view of the city and the Judean Hills. Abstract and modern pieces use Jerusalem as inspiration rather than documentation and suit a broader range of interiors. Kever Rachel and Eretz Yisrael pieces sit across all three categories depending on the style.',
    ],
  },
  {
    question: 'What is the difference between a Kotel painting, a print, and a mixed-media piece?',
    answer: [
      'A Kotel painting is an original, one-of-a-kind work created directly by the artist. A print is a high-quality reproduction of an original, available in multiple sizes and more accessible in price. Mixed-media pieces sit between the two: they begin as a print, then receive hand-finished details from the artist, such as texture, paint, gold leaf, or other additions that make the piece feel alive and three-dimensional. Mixed media offers something more elevated than a standard print, without the price of a full original. All three formats are available framed or unframed.',
    ],
  },
  {
    question: 'How do I choose between a classic Kotel scene and a more modern Jerusalem painting?',
    answer: [
      'Classic Kotel scenes and Old City landscapes anchor a space with something immediately recognisable. They work in most Jewish homes without needing explanation and tend to suit traditional or neutral interiors. Modern or abstract Jerusalem paintings are more flexible: they carry the meaning without the iconography and integrate naturally into contemporary spaces. The practical test is to look at what is already on your walls. If the room is traditional, a classic scene fits. If it is contemporary, a modern or abstract piece will feel more at home.',
    ],
  },
  {
    question: 'What size Jerusalem art should I buy?',
    answer: [
      'For most living room walls, a piece between 60 and 90 cm wide is the right starting point. Larger walls, particularly above a sofa or fireplace, can carry a piece 90 to 120 cm wide comfortably. Smaller pieces (40 to 60 cm) work well in hallways, studies, or as part of a grouping. Many listings on Joodaic include a wall preview tool: you can upload a photo of your space and see exactly how the artwork will look on your wall before committing, and experiment with different sizes of the same piece to find the best fit.',
    ],
  },
  {
    question: 'Is Jerusalem art a good housewarming or bar mitzvah gift?',
    answer: [
      'Yes. Jerusalem wall art, and Kotel paintings in particular, is one of the most common choices for housewarmings, bar and bat mitzvahs, and milestone birthdays in Jewish households. It carries clear meaning and suits a wide range of tastes depending on the style chosen. For a housewarming, a classic Jerusalem scene or Kotel painting tends to be the safest choice. For a bar or bat mitzvah, a piece with more colour and energy, or an abstract interpretation, can feel more personal and memorable.',
    ],
  },
  {
    question: 'What does Kever Rachel art represent?',
    answer: [
      'Kever Rachel refers to the Tomb of Rachel, located near Bethlehem and one of the most visited Jewish pilgrimage sites in the land of Israel. In Jewish tradition, Rachel is associated with compassion, intercession, and the longing for return. Her tomb is a place of prayer, particularly for those seeking comfort or divine intervention. Kever Rachel art carries that emotional and spiritual weight: it is both a sacred landscape and a devotional image. If you are drawn to a specific artist\'s interpretation, you can message them directly through their profile to commission a personalised Kever Rachel piece.',
    ],
  },
];

const getSchema = rootURL => {
  const classicsURL = `${rootURL}/s/art/the_classics`;
  return [
    {
      '@type': 'CollectionPage',
      '@id': classicsURL,
      name: 'Jerusalem Wall Art & Kotel Paintings',
      description:
        'Kotel paintings and Jerusalem wall art from independent Jewish artists, including original paintings, fine art prints, framed pieces, and mixed-media artwork.',
      url: classicsURL,
      isPartOf: { '@id': `${rootURL}/#website` },
      about: [
        { '@type': 'Thing', name: 'Jerusalem Art' },
        { '@type': 'Thing', name: 'Kotel Art' },
        { '@type': 'Thing', name: 'Jewish Wall Art' },
      ],
      publisher: { '@id': `${rootURL}/#organization` },
    },
    {
      '@type': 'BreadcrumbList',
      '@id': `${classicsURL}#breadcrumb`,
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: 'Home',
          item: `${rootURL}`,
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: 'Art',
          item: `${rootURL}/s/art`,
        },
        {
          '@type': 'ListItem',
          position: 3,
          name: 'Jerusalem & Kotel Art',
          item: classicsURL,
        },
      ],
    },
    {
      '@type': 'FAQPage',
      '@id': `${classicsURL}#faq`,
      mainEntity: FAQ_ITEMS.map(item => ({
        '@type': 'Question',
        name: item.question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: item.answer[0],
        },
      })),
    },
  ];
};

const theClassicsSeoConfig = {
  key: 'the_classics',
  match: ({ listingType, activeCollection }) =>
    listingType === 'art' && activeCollection === 'the_classics',
  accentColor: '--colorArt',
  links: [],
  title: 'Jerusalem Wall Art & Kotel Paintings | Prints & Originals | Joodaic',
  description:
    'Shop Jerusalem wall art and Kotel paintings by independent Jewish artists, including Western Wall scenes, Old City views, Kever Rachel art, originals, prints, and mixed-media pieces.',
  ogTitle: 'Jerusalem Wall Art & Kotel Paintings | Prints & Originals | Joodaic',
  ogDescription:
    'Shop Jerusalem wall art and Kotel paintings by independent Jewish artists, including Western Wall scenes, Old City views, Kever Rachel art, originals, prints, and mixed-media pieces.',
  twitterTitle: 'Jerusalem Wall Art & Kotel Paintings | Prints & Originals | Joodaic',
  twitterDescription:
    'Shop Jerusalem wall art and Kotel paintings by independent Jewish artists, including Western Wall scenes, Old City views, Kever Rachel art, originals, prints, and mixed-media pieces.',
  ogImage: {
    url: 'https://sharetribe.imgix.net/67c4adcd-cbbe-48c1-a752-e48bba4bb050/67e4261b-58fe-4028-9e36-dbfcca7d5afc?auto=format&fit=clip&h=1024&w=1024&s=53759b4b350cfb87030612981bb79847',
    width: '1024',
    height: '1024',
  },
  getSchema,
  intro: {
    heading: 'Jerusalem & Kotel Art',
    tagline:
      'Kotel paintings and Jerusalem wall art from independent Jewish artists, including original paintings, fine art prints, framed pieces, and mixed-media artwork. The collection ranges from classic Western Wall scenes and Old City views to Kever Rachel, Eretz Yisrael landscapes, and modern or abstract interpretations of Yerushalayim.',
    collapsedHeading: 'Jerusalem & Kotel Art',
    bodyParagraphs: [
      'Some pieces are chosen for the place. Others are chosen for the feeling Jerusalem brings into the room.',
    ],
  },
  bottomSections: [
    {
      id: 'positioning',
      type: 'prose',
      heading: 'Jerusalem art for the wall',
      paragraphs: [
        'Jerusalem is one of the most painted cities in the world. That also means it\'s one of the most reproduced — and the difference between a piece that becomes part of your home and one that disappears into the wall isn\'t always obvious.',
        'The works here range from classic Kotel scenes and Old City landscapes to more modern interpretations — pieces that use Jerusalem as a subject rather than a document. Some are quiet and devotional. Others bring colour, movement, or a more contemporary eye.',
        'Prices range across the collection, from fine art prints to original paintings and hand-finished mixed-media pieces. Use the Originals, Prints & Mixed Media filter above to narrow by format.',
        'Jerusalem art is a common choice for housewarmings, bar and bat mitzvahs, and milestone occasions, particularly for recipients with a personal connection to Israel or Yerushalayim.',
      ],
      paragraphsAfter: [
        'For more personal pieces, you can explore <a href="/s/art/gedolim">Gedolim portraits</a>.',
        'For Jerusalem and Kotel imagery paired with Hebrew text, browse <a href="/s/collection/signs?pub_sign_type=has_any%3Aisrael-jerusalem">Jewish signs and plaques with Israel and Jerusalem themes</a>.',
        'For Jerusalem art that centres people and Jewish life rather than the landscape, browse <a href="/s/art/jewish_icons">Jewish Life &amp; Tradition Art</a>.',
        'Looking for a specific view or subject? You can <a href="/artist/custom_co_studio">commission a Kotel or Jerusalem scene from a Joodaic artist</a>.',
      ],
    },
    {
      id: 'classic_kotel',
      title: 'Classic Kotel & Old City Art',
      paragraphs: [
        'Classic Kotel paintings and Old City scenes centre the Western Wall, the Jerusalem skyline, and the ancient gates in a traditional register: quiet, devotional, and immediately recognisable. These work well in living rooms, dining rooms, and entranceways where the subject speaks without explanation. Most pieces in this style are available as original paintings or fine art prints, framed or unframed.',
      ],
    },
    {
      id: 'jerusalem_landscapes',
      title: 'Jerusalem Landscapes & Scenic Art',
      paragraphs: [
        'Jerusalem landscape paintings capture the city from a wider view: the Judean Hills, the golden light of late afternoon, the texture of Jerusalem stone. These feel less specifically religious while staying grounded in place, and work across a broader range of interiors. Kever Rachel and the surrounding landscape also sit in this category.',
      ],
    },
    {
      id: 'modern_abstract',
      title: 'Modern & Abstract Jerusalem Art',
      paragraphs: [
        'Modern and abstract interpretations use Jerusalem as inspiration rather than documentation: more colour, more movement, more open to context. These are the most flexible pieces in the collection. They work in contemporary interiors where the art anchors a room without defining it, and tend to appeal to buyers who want the meaning without the iconography.',
      ],
    },
  ],
  faqHeading: 'FAQ',
  faq: FAQ_ITEMS,
};

export default theClassicsSeoConfig;
